import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Alert,
  Avatar,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  Save,
  LocationOn
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { decryptAppId, decryptText, encryptText, encryptAppId } from "../../../utils/encryption";
import LaboratoryService from '../../../services/laboratoryService';
import acProcessService from '../../../services/acProcessService';
import axios from 'axios';

const LabDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedAssessmentId = searchParams.get("assessmentId");

  // Decrypt parameters
  const appIdFromUrl = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgIdFromUrl = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const assessmentIdFromUrl = encryptedAssessmentId ? decryptText(encryptedAssessmentId) : null;

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appId] = useState(appIdFromUrl);
  const [orgId] = useState(orgIdFromUrl);
  const [assessmentId] = useState(assessmentIdFromUrl);
  const [labTypeId, setLabTypeId] = useState('');
  const [labName, setLabName] = useState('');
  const [country, setCountry] = useState('India');

  // Address fields
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [subDistrict, setSubDistrict] = useState('');
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');

  // Contact Person fields
  const [salutation, setSalutation] = useState('Mr.');
  const [contactName, setContactName] = useState('');
  const [designation, setDesignation] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [landlineCode, setLandlineCode] = useState('');
  const [landlineNo, setLandlineNo] = useState('');
  const [faxCode, setFaxCode] = useState('');
  const [faxNo, setFaxNo] = useState('');
  const [email, setEmail] = useState('');

  // Dropdown data
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subDistricts, setSubDistricts] = useState([]);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load data on mount
  useEffect(() => {
    const fetchData = async () => {
      await loadStates();
      if (appId) {
        await initializePage();
      }
    };
    fetchData();
  }, [appId]);

  const initializePage = async () => {
    try {
      setLoading(true);
      
      // Load states first and get the array
      const loadedStates = await loadStates();

      // First, get application details to retrieve labTypeId
      const appResponse = await acProcessService.getApplicationDetails(appId);
      if (appResponse && appResponse.data && appResponse.data.LabTypeId) {
        const fetchedLabTypeId = appResponse.data.LabTypeId;
        setLabTypeId(fetchedLabTypeId);
        
        // Now fetch lab details using the new API
        const response = await axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/AcprocessLabdetails`,
          {
            params: {
              appid: appId,
              labtypeid: fetchedLabTypeId
            }
          }
        );
        
        console.log('Lab details API response:', response.data);
        
        if (response.data && response.data.isstatus) {
          // Pass the loaded states array directly
          await populateFormData(response.data, loadedStates);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error initializing page:', err);
      setError(err.message || 'Failed to load page data');
      setLoading(false);
    }
  };

  const loadStates = async () => {
    try {
      const result = await LaboratoryService.getStates();
      if (result && result.success && result.data) {
        setStates(result.data);
        console.log('States loaded:', result.data.length);
        if (result.data.length > 0) {
          console.log('First state example:', result.data[0]);
        }
        return result.data; // Return the loaded states
      } else {
        console.error('Failed to load states:', result);
        showSnackbar('Failed to load states', 'error');
        return [];
      }
    } catch (error) {
      console.error('Error loading states:', error);
      showSnackbar('Error loading states', 'error');
      return [];
    }
  };

  const loadCities = async (stateId) => {
    try {
      console.log('Loading cities for state ID:', stateId);
      const result = await LaboratoryService.getCitiesByState(stateId);
      if (result && result.success && result.data) {
        setCities(result.data);
        console.log('Cities loaded:', result.data.length);
        if (result.data.length > 0) {
          console.log('First city example:', result.data[0]);
        }
      } else {
        setCities([]);
        console.error('Failed to load cities:', result);
      }
    } catch (error) {
      console.error('Error loading cities:', error);
      setCities([]);
    }
  };

  const loadDistricts = async (stateCode) => {
    try {
      console.log('Loading districts for state code:', stateCode);
      const result = await LaboratoryService.getDistrictsByState(stateCode);
      if (result && result.success && result.data) {
        setDistricts(result.data);
        console.log('Districts loaded:', result.data.length);
        if (result.data.length > 0) {
          console.log('First district example:', result.data[0]);
        }
      } else {
        setDistricts([]);
        console.error('Failed to load districts:', result);
      }
    } catch (error) {
      console.error('Error loading districts:', error);
      setDistricts([]);
    }
  };

  const loadSubDistricts = async (districtCode) => {
    try {
      const result = await LaboratoryService.getSubDistrictsByDistrict(districtCode);
      if (result && result.success && result.data) {
        setSubDistricts(result.data);
        console.log('Sub-districts loaded:', result.data.length);
      } else {
        setSubDistricts([]);
        console.error('Failed to load sub-districts:', result);
      }
    } catch (error) {
      console.error('Error loading sub-districts:', error);
      setSubDistricts([]);
    }
  };

  const populateFormData = async (data, statesArray = null) => {
    try {
      // Use passed statesArray or fall back to component state
      const availableStates = statesArray || states;
      console.log('Populating form with data:', data);
      console.log('Available states:', availableStates.length);
      
      // Populate from new API response format
      setCountry(data.country || 'India');
      setLabName(data.nameLaboratory || '');
      setAddress(data.address || '');
      setPincode(data.pinCode || '');

      // Populate contact person
      setContactName(data.name || '');
      setDesignation(data.designation || '');
      setMobileNo(data.mobileNo || '');
      setEmail(data.email || '');

      // Parse landline
      if (data.landline) {
        const [code, number] = data.landline.split('-');
        setLandlineCode(code || '');
        setLandlineNo(number || '');
      }

      // Parse fax
      if (data.fax) {
        const [code, number] = data.fax.split('-');
        setFaxCode(code || '');
        setFaxNo(number || '');
      }

      // Load state-dependent dropdowns in sequence
      if (data.state) {
        // Convert to number for comparison
        const stateIdFromApi = parseInt(data.state);
        console.log('Setting state ID:', stateIdFromApi);
        
        // Set state value (ensure it matches dropdown value type)
        setState(stateIdFromApi);
        
        // Find selected state from available states
        const selectedState = availableStates.find(s => parseInt(s.id) === stateIdFromApi);
        console.log('Found state:', selectedState);
        
        if (selectedState) {
          // Load cities for selected state and wait for completion
          await loadCities(selectedState.id);
          
          // Set city value after cities are loaded
          if (data.city) {
            const cityIdFromApi = parseInt(data.city);
            console.log('Setting city ID:', cityIdFromApi);
            setCity(cityIdFromApi);
          }
          
          // Get state code for loading districts
          const stateCode = selectedState.State_Code || selectedState.code || selectedState.state_code || selectedState.state_id || selectedState.id;
          console.log('Loading districts with state code:', stateCode);
          
          if (stateCode) {
            await loadDistricts(stateCode);
            
            // Set district value after districts are loaded
            if (data.district) {
              const districtCodeFromApi = data.district.toString();
              console.log('Setting district code:', districtCodeFromApi);
              setDistrict(districtCodeFromApi);
              
              // Load sub-districts for selected district
              await loadSubDistricts(districtCodeFromApi);
            }
          }
        } else {
          console.error('State not found in loaded states array');
        }
      }
    } catch (err) {
      console.error('Error populating form data:', err);
    }
  };

  const handleStateChange = async (event) => {
    const stateId = event.target.value;
    setState(stateId);
    setCity('');
    setDistrict('');
    setSubDistrict('');
    
    await loadCities(stateId);
    
    // Get state code for loading districts
    const selectedState = states.find(s => s.id == stateId);
    if (selectedState) {
      const stateCode = selectedState.State_Code || selectedState.code || selectedState.state_code || selectedState.state_id || selectedState.id;
      if (stateCode) {
        await loadDistricts(stateCode);
      }
    }
  };

  const handleDistrictChange = async (event) => {
    const districtCode = event.target.value;
    setDistrict(districtCode);
    setSubDistrict('');
    
    await loadSubDistricts(districtCode);
  };

  const handleUpdate = async () => {
    try {
      // Validate required fields
      if (!state) {
        showSnackbar('Please select State', 'warning');
        return;
      }
      if (!address.trim()) {
        showSnackbar('Please enter Address', 'warning');
        return;
      }
      if (!pincode.trim()) {
        showSnackbar('Please enter Pin Code', 'warning');
        return;
      }
      if (!labTypeId) {
        showSnackbar('Lab Type ID is missing', 'error');
        return;
      }

      const updateData = {
        isstatus: true,
        message: '',
        appid: String(appId),
        labtypeid: String(labTypeId),
        country: '101',
        state: String(state),
        city: String(city),
        address: String(address),
        district: String(district),
        pinCode: String(pincode),
        nameLaboratory: String(labName),
        name: String(contactName),
        designation: String(designation),
        mobileNo: String(mobileNo),
        landline: landlineCode && landlineNo ? `${String(landlineCode)}-${String(landlineNo)}` : '',
        fax: faxCode && faxNo ? `${String(faxCode)}-${String(faxNo)}` : '',
        email: String(email),
        updatedby: localStorage.getItem('orgId') || '0'
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/AcprocessLabdetailsupdate`,
        updateData
      );

      if (response.data && response.data.isstatus) {
        showSnackbar(response.data.message || 'Laboratory details updated successfully', 'success');
        
        // Navigate back after a delay
        setTimeout(() => {
          navigate(`/main/do/decision-process?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}`);
        }, 1500);
      } else {
        showSnackbar(response.data?.message || 'Failed to update laboratory details', 'error');
      }

    } catch (err) {
      console.error('Error updating lab details:', err);
      showSnackbar(err.response?.data?.message || err.message || 'Failed to update laboratory details', 'error');
    }
  };

  // Helper function to handle numeric-only input with length restrictions
  const handleNumericInput = (value, maxLength) => {
    // Remove any non-numeric characters and limit to maxLength
    const numericValue = value.replace(/\D/g, '').slice(0, maxLength);
    return numericValue;
  };

  // Enhanced handleChange function for numeric fields
  const handleNumericChange = (fieldName, value, maxLength) => {
    const numericValue = handleNumericInput(value, maxLength);
    handleChange(fieldName, numericValue);
  };

  const handleChange = (fieldName, value) => {
    switch (fieldName) {
      case 'pincode':
        setPincode(value);
        break;
      case 'mobileNo':
        setMobileNo(value);
        break;
      default:
        break;
    }
  }

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };


//   if (loading) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
//         <Typography>Loading...</Typography>
//       </Box>
//     );
//   }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          onClick={() => navigate(`/main/do/decision-process?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}`)} 
          startIcon={<ArrowBack />} 
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", mr: 2, width: 56, height: 56 }}>
                <LocationOn sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Laboratory Details
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  Contact Information and Address
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={() => navigate(`/main/do/decision-process?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}`)}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" }
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>

          {/* Address Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 2 }}>
              Laboratory Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Laboratory Name"
              value={labName}
              onChange={(e) => setLabName(e.target.value)}
            />
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </Grid> */}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>State/Province</InputLabel>
              <Select
                value={state}
                onChange={handleStateChange}
                label="State/Province"
              >
                <MenuItem value="">--Select State--</MenuItem>
                {states.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name || s.State_Name_Eng || s.stateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>City</InputLabel>
              <Select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                label="City"
                disabled={!state}
              >
                <MenuItem value="">--Select City--</MenuItem>
                {cities.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name || c.City_Name || c.cityName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>District</InputLabel>
              <Select
                value={district}
                onChange={handleDistrictChange}
                label="District"
                disabled={!state}
              >
                <MenuItem value="">--Select District--</MenuItem>
                {districts.map((d) => (
                  <MenuItem key={d.District_Code} value={d.District_Code}>
                    {d.District_Name_Eng}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Sub District</InputLabel>
              <Select
                value={subDistrict}
                onChange={(e) => setSubDistrict(e.target.value)}
                label="Sub District"
                disabled={!district}
              >
                <MenuItem value="">--Select Sub District--</MenuItem>
                {subDistricts.map((sd) => (
                  <MenuItem key={sd.Sub_District_Code} value={sd.Sub_District_Code}>
                    {sd.Sub_District_Name_Eng}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid> */}

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Pin Code"
              value={pincode}
              onChange={(e) => handleNumericChange('pincode', e.target.value, 6)}
              inputProps={{ maxLength: 6 }}
            />
          </Grid>

          {/* Contact Person Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mt: 2 }}>
              Contact Person for NABL
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ minWidth: 100 }}>
                <InputLabel>Title</InputLabel>
                <Select
                  value={salutation}
                  onChange={(e) => setSalutation(e.target.value)}
                  label="Title"
                >
                  <MenuItem value="Dr.">Dr.</MenuItem>
                  <MenuItem value="Mr.">Mr.</MenuItem>
                  <MenuItem value="Ms.">Ms.</MenuItem>
                  <MenuItem value="Mrs.">Mrs.</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                required
                label="Name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Designation"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Mobile No"
              value={mobileNo}
              onChange={(e) => handleNumericChange('mobileNo', e.target.value, 10)}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="STD Code"
                value={landlineCode}
                onChange={(e) => setLandlineCode(e.target.value)}
                sx={{ width: 120 }}
                inputProps={{ maxLength: 5 }}
              />
              <TextField
                fullWidth
                label="Landline No"
                value={landlineNo}
                onChange={(e) => setLandlineNo(e.target.value)}
                inputProps={{ maxLength: 10 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="STD Code"
                value={faxCode}
                onChange={(e) => setFaxCode(e.target.value)}
                sx={{ width: 120 }}
                inputProps={{ maxLength: 5 }}
              />
              <TextField
                fullWidth
                label="Fax No"
                value={faxNo}
                onChange={(e) => setFaxNo(e.target.value)}
                inputProps={{ maxLength: 10 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={email && !validateEmail(email)}
              helperText={email && !validateEmail(email) ? "Invalid email format" : ""}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sx={{ textAlign: "center", mt: 4 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                startIcon={<ArrowBack />}
                onClick={() => navigate(`/main/do/decision-process?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(assessmentId)}`)}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<Save />}
                onClick={handleUpdate}
              >
                Update
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LabDetails;
